// src/Gallery.js
import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiPlay, FiPause, FiVolumeX, FiVolume2 } from "react-icons/fi";
import { Link } from "react-router-dom";

import gameboy from "./assets/gameboy.png";

import bunnycloud from './assets/bunnycloud.png';
import beanbean from './assets/beanbean.png';
import butterfly from './assets/butterfly.png';
import tomatocats from './assets/tomatocats.jpg';


const items = [
  { id: 1, image: bunnycloud, title: 'Bunny Cloud', description: 'A fluffy bunny cloud.' },
  { id: 8, image: beanbean, title: 'Bean Bean', description: 'A cute bean character.' },
  { id: 9, image: butterfly, title: 'catfly', description: 'Colorful butterfly image.' },
  { id: 10, image: tomatocats, title: 'Pocket Home', description: 'Pocket home design.' },
];

const videoItems = [
  { id: 1, videoUrl: "https://player.vimeo.com/video/904990901", title: "Metamorphosing Stripes" },
  { id: 2, videoUrl: "https://player.vimeo.com/video/895652634", title: "Spider Web Spiderweb Spider's Web" },
  { id: 3, videoUrl: "https://player.vimeo.com/video/1021821249", title: "Minathan" },
  { id: 4, videoUrl: "https://player.vimeo.com/video/1021819637", title: "Mindy Land" },
  { id: 5, videoUrl: "https://player.vimeo.com/video/1117214244", title: "Mindy Frog" },
];

const getVimeoId = (url) => (url.match(/video\/(\d+)/)?.[1] ?? null);

export default function Gallery() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [tab, setTab] = useState("photos");

  const mountRef = useRef(null);        // where Vimeo injects its iframe
  const playerRef = useRef(null);       // Vimeo.Player
  const clickTimerRef = useRef(null);   // single vs double click gate

  // ---- NEW: overlay state to show native Vimeo controls
  const [showVimeoFS, setShowVimeoFS] = useState(false);
  const wasPlayingRef = useRef(false);
  const [overlayStartSec, setOverlayStartSec] = useState(0);

  // Create the Vimeo player ONCE (SDK injects iframe). No initial iframe src -> no stutter.
  useEffect(() => {
    const create = () => {
      if (!mountRef.current) return;
      const initialId = getVimeoId(videoItems[0].videoUrl);

      const p = new window.Vimeo.Player(mountRef.current, {
        id: initialId,
        autoplay: true,
        muted: true,     // guarantees autoplay on mobile
        loop: true,
        controls: false, // keep Game Boy clean; overlay will show native controls
        dnt: true,
        playsinline: true,
        autopause: false,
      });

      playerRef.current = p;

      // keep UI state in sync
      p.on("play",  () => setPaused(false));
      p.on("pause", () => setPaused(true));

      // Start muted; ensure we call play() once ready, then reflect UI
      Promise.allSettled([ p.setVolume(0), p.ready?.() ?? Promise.resolve() ])
        .finally(() => {
          p.play().then(() => setPaused(false)).catch(() => {});
        });
    };

    if (window.Vimeo?.Player) {
      create();
    } else {
      const s = document.createElement("script");
      s.src = "https://player.vimeo.com/api/player.js";
      s.async = true;
      s.onload = create;
      document.body.appendChild(s);
    }

    return () => {
      try { playerRef.current?.destroy(); } catch {}
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  // Change video on idx change — only reload if different from current
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    const wantedId = getVimeoId(videoItems[idx].videoUrl);
    if (!wantedId) return;
  
    p.getVideoId()
      .then((currentId) => {
        if (String(currentId) === String(wantedId)) {
          // same video; only adjust volume
          return p.setVolume(muted ? 0 : 1);
        }
        return p
          .loadVideo(wantedId)
          .then(() => p.setVolume(muted ? 0 : 1))
          .then(() => p.play())
          .then(() => { setPaused(false); }); // <- now chained correctly
      })
      .catch(() => {});
  }, [idx, muted]);
  

  // Mute toggle w/o affecting play/pause state
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    p.setVolume(muted ? 0 : 1).catch(() => {});
  }, [muted]);

  // Controls
  const nextVideo = () => {
    setPaused(false);
    setIdx((i) => (i + 1) % videoItems.length);
  };
  const prevVideo = () => {
    setPaused(false);
    setIdx((i) => (i - 1 + videoItems.length) % videoItems.length);
  };
  

  const togglePlay = async () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      const isPaused = await p.getPaused();
      if (isPaused) {
        await p.play();
        setPaused(false);
      } else {
        await p.pause();
        setPaused(true);
      }
    } catch {}
  };

  const toggleSound = () => setMuted((m) => !m);

  // ---- NEW: open/close overlay that shows native Vimeo controls
  const openVimeoOverlay = async () => {
    const p = playerRef.current;
    try {
      const [isPaused, t] = await Promise.all([
        p?.getPaused() ?? Promise.resolve(true),
        p?.getCurrentTime() ?? Promise.resolve(0),
      ]);
      wasPlayingRef.current = !isPaused;
      setOverlayStartSec(Math.floor(t || 0));
      if (p && !isPaused) {
        await p.pause();         // pause inline so you don't get double audio
        setPaused(true);
      }
    } catch {}
    setShowVimeoFS(true);
  };

  const closeVimeoOverlay = async () => {
    setShowVimeoFS(false);
    const p = playerRef.current;
    if (p && wasPlayingRef.current) {
      try { await p.play(); setPaused(false); } catch {}
    }
  };

  // ESC to close overlay
  useEffect(() => {
    if (!showVimeoFS) return;
    const onKey = (e) => { if (e.key === "Escape") closeVimeoOverlay(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showVimeoFS]);

  // Overlay: single click = toggle play; double click = open overlay with native controls
  const handleOverlayClick = () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      togglePlay();
      clickTimerRef.current = null;
    }, 220);
  };

  const handleOverlayDoubleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    openVimeoOverlay();
  };

  return (
    <>
      {/* Game Boy hero (inline player with custom controls) */}
      <div className="gameboy-wrap">
        <img src={gameboy} alt="Game Boy" className="gameboy-frame" />
        <div className="gameboy-screen" role="group" aria-label="Game Boy video">
          <div ref={mountRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
          <button
            type="button"
            onClick={handleOverlayClick}
            onDoubleClick={handleOverlayDoubleClick}
            aria-label={paused ? "Play video" : "Pause video"}
            style={{ position: "absolute", inset: 0, background: "transparent", border: 0, cursor: "pointer", zIndex: 2 }}
          />
        </div>

        {/* D-Pad / A / B buttons (unchanged) */}
        <button className="gba-hotspot dpad-left"  onClick={prevVideo} aria-label="Previous video"><FiChevronLeft size={18} /></button>
        <button className="gba-hotspot dpad-right" onClick={nextVideo} aria-label="Next video"><FiChevronRight size={20} /></button>
        <button className="gba-hotspot btn-a" onClick={togglePlay} aria-label={paused ? "Play" : "Pause"}>
          {paused ? <FiPlay size={20}/> : <FiPause size={20}/>}
        </button>
        <button className="gba-hotspot btn-b" onClick={toggleSound} aria-label="Toggle sound">
          {muted ? <FiVolumeX size={20}/> : <FiVolume2 size={20}/>}
        </button>
      </div>

      {/* Cute segmented control */}
      <div className={`seg ${tab}`}>
        <span className="seg-indicator" aria-hidden="true" />
        <button
          className={`seg-btn ${tab === "photos" ? "on" : ""}`}
          onClick={() => setTab("photos")}
          role="tab"
          aria-selected={tab === "photos"}
        >
          Photos
        </button>
        <button
          className={`seg-btn ${tab === "videos" ? "on" : ""}`}
          onClick={() => setTab("videos")}
          role="tab"
          aria-selected={tab === "videos"}
        >
          Videos
        </button>
      </div>

      {/* Photos (masonry) */}
      {tab === "photos" && (
        <div className="gallery-masonry">
          {items.map((item) => (
            <Link to={`/art/${item.id}#focus`} key={item.id} className="mitem">
            <img src={item.image} alt={item.title} loading="lazy" />
          </Link>
          ))}
        </div>
      )}


      {/* Videos (clean cards) */}
      {tab === "videos" && (
        <div className="video-grid">
          {videoItems.map((video) => (
            <div className="video-card" key={video.id}>
              <iframe
                src={`${video.videoUrl}?title=0&byline=0&portrait=0&color=00dcd1&dnt=1`}
                loading="lazy"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.title}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
